import { Alert, Button, Col, Flex, Form, Row, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { confirmMFAPendingAppSetup } from '@/api/mfa';
import { isAuthErrorResponse } from '@/api/user';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { useApiMutation } from '@/hooks/api';
import { useMFAPendingAppSetup } from '@/hooks/queries/useMFAPendingAppSetup';
import { SPACE_MD, SPACE_XL } from '@/theme/buildAntdTheme';
import FormErrorAlert from '@/UI/FormErrorAlert';
import Loader from '@/UI/Loader';
import { SubsectionHeading } from '@/UI/typography';
import { buildMfaFormSchema } from '@/utils/validation/formSchemas/authSchemas';

import MFABackupCodesPanel from './MFABackupCodesPanel';

import type { AuthTokenResponse } from '@/api/user';
import type { SchemaSection } from '@/components/form/SchemaForm/types';

const QR_SIZE = 160;

type Step = 'setup' | 'backup';

interface MFAAppSetupInLoginProps {
  mfaToken: string;
  onSuccess: (loginData: AuthTokenResponse) => void;
  onBack: () => void;
}

/**
 * Authenticator-app enrolment inside the login flow. Confirming the device also
 * completes the login, so the auth token is held until the backup codes are saved.
 */
const MFAAppSetupInLogin = ({
  mfaToken,
  onSuccess,
  onBack,
}: MFAAppSetupInLoginProps) => {
  const [t] = useTranslation();
  const [form] = Form.useForm();
  const [step, setStep] = useState<Step>('setup');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loginData, setLoginData] = useState<AuthTokenResponse | null>(null);
  const [error, setError] = useState('');

  const {
    data: setupData,
    isPending: loadingSetup,
    isError: setupFailed,
  } = useMFAPendingAppSetup(mfaToken);

  const confirmMutation = useApiMutation({
    mutationFn: (otpToken: string) =>
      confirmMFAPendingAppSetup(mfaToken, otpToken),
    errorKey: 'MFA_INVALID_CODE',
    onSuccess: (result) => {
      if (!result || isAuthErrorResponse(result)) {
        setError(t('MFA_INVALID_CODE'));
        return;
      }
      if (!result.access_token) {
        setError(t('MFA_SETUP_FAILED'));
        return;
      }
      setBackupCodes(result.backup_codes);
      setLoginData(result);
      setStep('backup');
    },
  });

  const formSchema = useMemo(() => buildMfaFormSchema(t), [t]);

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'mfa-app-setup',
        fields: [
          {
            name: 'otp_token',
            type: 'text',
            label: t('MFA_CODE_LABEL'),
            componentProps: {
              placeholder: '000000',
              maxLength: 20,
              autoFocus: true,
              autoComplete: 'one-time-code',
              size: 'large',
            },
          },
        ],
      },
    ],
    [t],
  );

  if (step === 'backup' && loginData) {
    return (
      <Flex vertical gap={SPACE_MD}>
        <SubsectionHeading>{t('MFA_SETUP_DONE_TITLE')}</SubsectionHeading>
        <Alert
          type="warning"
          showIcon={false}
          title={t('MFA_BACKUP_CODES_WARNING_TITLE')}
          description={t('MFA_BACKUP_CODES_WARNING')}
        />
        <MFABackupCodesPanel codes={backupCodes} />
        <Button
          type="primary"
          size="large"
          block
          onClick={() => onSuccess(loginData)}
        >
          {t('MFA_BACKUP_DONE_BUTTON')}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex vertical gap={SPACE_MD}>
      <SubsectionHeading>{t('MFA_APP_SETUP_TITLE')}</SubsectionHeading>
      <Typography.Text type="secondary">
        {t('MFA_APP_SETUP_INSTRUCTION')}
      </Typography.Text>

      {loadingSetup ? <Loader centered /> : null}

      {setupFailed ? <FormErrorAlert error={t('MFA_SETUP_ERROR')} /> : null}

      {setupData ? (
        <>
          <Row gutter={SPACE_XL} align="top">
            <Col>
              <img
                src={`data:image/png;base64,${setupData.qr_code}`}
                alt={t('MFA_APP_SETUP_TITLE')}
                style={{ width: QR_SIZE, height: QR_SIZE }}
              />
            </Col>
            <Col flex="auto">
              <Flex vertical gap="small" align="flex-start">
                <Typography.Text type="secondary">
                  {t('MFA_MANUAL_ENTRY_LABEL')}
                </Typography.Text>
                <Typography.Text code copyable>
                  {setupData.secret}
                </Typography.Text>
              </Flex>
            </Col>
          </Row>

          <FormErrorAlert
            error={error || confirmMutation.error}
            fallbackKey="MFA_INVALID_CODE"
          />
          <SchemaForm
            form={form}
            formSchema={formSchema}
            name="mfa-app-setup"
            onFinish={({ otp_token }: { otp_token: string }) => {
              setError('');
              confirmMutation.mutate(otp_token);
            }}
            sections={sections}
            footer={{
              actionSize: 'large',
              actions: [
                {
                  label: t('MFA_ENABLE_BUTTON'),
                  htmlType: 'submit',
                  type: 'primary',
                  loading: confirmMutation.loading,
                },
              ],
            }}
          />
        </>
      ) : null}

      <Typography.Link onClick={onBack}>
        ← {t('MFA_BACK_TO_LOGIN')}
      </Typography.Link>
    </Flex>
  );
};

export default MFAAppSetupInLogin;
